import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService, { LivestreamSettings } from '../services/api';

const LiveBanner: React.FC = () => {
  const [livestream, setLivestream] = useState<LivestreamSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    // Check sessionStorage for dismiss state
    const dismissedId = sessionStorage.getItem('liveBannerDismissed');
    return dismissedId !== null;
  });

  useEffect(() => {
    const fetchLivestream = async () => {
      try {
        const data = await apiService.getLivestream();
        setLivestream(data);

        // If the stream changed (different vimeoId), reset dismiss state
        const dismissedId = sessionStorage.getItem('liveBannerDismissed');
        if (dismissedId && data.vimeoId && dismissedId !== data.vimeoId) {
          sessionStorage.removeItem('liveBannerDismissed');
          setDismissed(false);
        }
      } catch (err) {
        console.error('Error fetching livestream:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLivestream();

    // Refresh every 60 seconds
    const interval = setInterval(fetchLivestream, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleDismiss = () => {
    // Store the current vimeoId so dismiss persists for this stream
    if (livestream?.vimeoId) {
      sessionStorage.setItem('liveBannerDismissed', livestream.vimeoId);
    } else {
      sessionStorage.setItem('liveBannerDismissed', 'scheduled');
    }
    setDismissed(true);
  };

  // Don't show if loading or dismissed
  if (loading || dismissed) {
    return null;
  }

  // Check if live
  const isLive = livestream?.isLive && livestream?.vimeoId;

  // Check if scheduled for future (vimeoId optional for scheduled announcements)
  const isScheduled = livestream?.scheduledFor && !livestream.isLive &&
    new Date(livestream.scheduledFor) > new Date();

  // Don't show if neither live nor scheduled
  if (!isLive && !isScheduled) {
    return null;
  }

  // Format scheduled date
  const formatScheduledDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 1) {
      return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return `Jutro o ${date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffHours > 0) {
      return `Za ${diffHours}h`;
    } else {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `Za ${diffMins} min`;
    }
  };

  // Live banner
  if (isLive) {
    return (
      <div className="bg-gradient-to-r from-red-600 to-red-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Live indicator */}
              <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold">NA ŻYWO</span>
              </div>

              {/* Title */}
              <div className="hidden sm:block">
                <p className="font-medium">
                  {livestream?.title || 'Transmisja na żywo'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Watch button */}
              <Link
                to="/live"
                className="inline-flex items-center px-4 py-1.5 bg-white text-red-600 rounded-full text-sm font-semibold hover:bg-red-50 transition-colors"
              >
                <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                Oglądaj
              </Link>

              {/* Dismiss button */}
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Zamknij"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Scheduled banner
  return (
    <div className="bg-gradient-to-r from-brand-600 to-brand-500 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Scheduled indicator */}
            <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-semibold">
                {formatScheduledDate(livestream!.scheduledFor!)}
              </span>
            </div>

            {/* Title */}
            <div className="hidden sm:block">
              <p className="font-medium">
                {livestream?.title || 'Nadchodząca transmisja'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Details button */}
            <Link
              to="/live"
              className="inline-flex items-center px-4 py-1.5 bg-white text-brand-600 rounded-full text-sm font-semibold hover:bg-brand-50 transition-colors"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Szczegóły
            </Link>

            {/* Dismiss button */}
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Zamknij"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveBanner;
